import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

// ----------------------------------------------------------------------

export type ThemeModePreference = "light" | "dark" | "system";
export type ResolvedThemeMode = "light" | "dark";

const THEME_MODE_KEY = "proativos-theme";
/** Chave usada por uma versão anterior do toggle — migrada na primeira leitura */
const LEGACY_THEME_MODE_KEY = "themeMode";

interface ThemeModeContextValue {
  /** Preferência escolhida pelo usuário (inclui "system") */
  preference: ThemeModePreference;
  /** Modo efetivo aplicado na UI ("system" já resolvido) */
  resolvedMode: ResolvedThemeMode;
  setPreference: (preference: ThemeModePreference) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

const migrateLegacyPreference = (): string | null => {
  const legacy = localStorage.getItem(LEGACY_THEME_MODE_KEY);
  if (legacy !== null) {
    localStorage.setItem(THEME_MODE_KEY, legacy);
    localStorage.removeItem(LEGACY_THEME_MODE_KEY);
  }
  return legacy;
};

const getStoredPreference = (): ThemeModePreference => {
  const stored = localStorage.getItem(THEME_MODE_KEY) ?? migrateLegacyPreference();
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  // Padrão claro até as telas internas estarem 100% adaptadas ao escuro;
  // o usuário pode optar por escuro/automático no toggle da sidebar
  return "light";
};

const getSystemMode = (): ResolvedThemeMode =>
  window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemeModePreference>(getStoredPreference);
  const [systemMode, setSystemMode] = useState<ResolvedThemeMode>(getSystemMode);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mediaQuery) return;

    const handleChange = (event: MediaQueryListEvent) => {
      setSystemMode(event.matches ? "dark" : "light");
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const setPreference = useCallback((next: ThemeModePreference) => {
    localStorage.setItem(THEME_MODE_KEY, next);
    setPreferenceState(next);
  }, []);

  const resolvedMode = preference === "system" ? systemMode : preference;

  const value = useMemo(
    () => ({ preference, resolvedMode, setPreference }),
    [preference, resolvedMode, setPreference]
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

export function useThemeMode(): ThemeModeContextValue {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error("useThemeMode deve ser usado dentro de ThemeModeProvider");
  }
  return context;
}
